"use client"

import { use, useEffect, useMemo, useRef, useState } from "react";
import useOnClickOutside from "../../hooks/useOnClickOutside.hook";
import SvgExpandLess from "../svgs/expand-less";
import SvgExpandMore from "../svgs/expand-more";
import useTranslation from "../../hooks/useTranslate.hook";
import ButtonExpand from "components/buttons/expand";
import style from './style.module.scss';
import ValidationHelper from "@/utils/validation.util";
import TooltipV2 from "components/tooltip-v2";
import { useIsClient } from "hooks/useIsClient.hook";
import SvgCheck from "components/svgs/check";
import { Colors } from "@/enums/colors.enum";
import SvgCancel from "components/svgs/cancel";
import ButtonDropdown from "components/buttons/dropdown";
import SvgInfo from 'components/svgs/info';


type SelectType = {
    title?: string;
    placeholder: string;
    data: any;
    defaultValue?: any;
    propertiesApi?: Array<string>;
    defaultPropertyName?: string;
    disabled?: boolean;
    disableTyping?: boolean;
    disableRemoveAll?: boolean;
    disabledItems?: Array<number>;
    disabledItemsVisible?: boolean;
    isError?: boolean;
    isLoading?: boolean;
    helpText?: string;
    errorText?: string;
    customClass?: any;
    onChange: Function;
    onClosed?: () => void;
    autoFocus?: boolean;
    onSearchChange?: Function;
    onEnter?: Function;
    fakeSelection?: string;
    noDataMessage?: string;
    styles?: any;
    openOnInit?: boolean;
    onEscapePress?: () => void;
    disableAutoComplete?: boolean;
    onSelectAll?: (data: any) => void;
    showInfoIcon?: boolean;
    infoIconText?: string;
};

export interface PositionIterface {
    bottom?: number;
    height?: number;
    left?: number;
    right?: number;
    top?: number;
    width?: number;
    x?: number;
    y?: number;
}

const Select = ({
    customClass,
    data,
    defaultValue,
    defaultPropertyName,
    disabled,
    isLoading = false,
    placeholder,
    propertiesApi,
    title,
    onChange,
    disableTyping = false,
    disableRemoveAll = false,
    disabledItems,
    disabledItemsVisible = false,
    helpText,
    isError,
    errorText,
    onClosed,
    autoFocus = false,
    onSearchChange,
    onEnter,
    fakeSelection,
    noDataMessage,
    styles,
    openOnInit = false,
    onEscapePress,
    disableAutoComplete = false,
    onSelectAll,
    showInfoIcon = false,
    infoIconText = "",
}: SelectType) => {

    const { t } = useTranslation();
    const ref = useRef<any>(null);
    const refInputWrap = useRef<HTMLDivElement>(null);
    const refInputField = useRef<HTMLInputElement>(null);
    const refKid = useRef<any>(null);
    const firstItemRef = useRef<any>(null);
    const isClient = useIsClient();

    const [isOpen, setIsOpen] = useState(openOnInit);
    const [selectedItem, setSelectedItem] = useState('');
    const [selected, setSelected] = useState<any>(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [searchValue, setSearchValue] = useState('');
    const [filteredData, setFilteredData] = useState<any>([]);
    const [position, setPosition] = useState<PositionIterface>({});
    const [inputFieldTouched, setInputFieldTouched] = useState(false);

    const windowCheck = typeof window !== 'undefined';
    useOnClickOutside(ref, () => {
        setIsOpen(false);
        changeSearchValue('');
        setSearchTerm('');
    });

    const updateIsOpen = (isOpen: boolean) => {
        if (!isOpen && !!onClosed) {
            onClosed();
        }
        setIsOpen(isOpen);
    }

    const handleSelection = (item: any) => {
        const title = propertiesApi ? propertiesApi?.map((prop: any) => item[prop] !== null ? item[prop] : '').join(" ") : item.name;
        setSelectedItem(title);
        changeSearchValue("");
        setSelected(item);
        onChange(item);
        updateIsOpen(false);
        setInputFieldTouched(false);
    };

    const handleRemoveItem = () => {
        setSelected(null);
        setSelectedItem('');
        setSearchTerm('');
        changeSearchValue('');
        setIsOpen(false);
        onChange({ id: null, name: null });
    }

    const changeSearchValue = (value: any) => {
        setSearchValue(value);
        if (!!onSearchChange) onSearchChange(value);
    }

    const positionTop = useMemo(() => {
        return ((position.top || 0) + 300) > (windowCheck ? window.innerHeight - 100 : 0) ?
            ((position?.top || 0) - (refKid?.current?.clientHeight || 0) - 16) :
            ((position?.y || 0) + (position?.height || 0) + (!!title ? 50 : 10));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [position]);

    const query = searchValue.trim().toLowerCase().replace(/\s\s+/g, ' ');

    const matchingItems = filteredData && filteredData.length > 0 && filteredData?.filter((filterItem: any) => {
        const name = ValidationHelper.isPropertyNullOrEmpty(filterItem?.name_translated) ? filterItem?.name?.toString().trim().toLowerCase() : filterItem?.name_translated?.toString().trim().toLowerCase();
        if (name === '' && query === '') {
            return true;
        } else if (!name) {
            return false;
        }

        if (query === '' || name.includes(query)) {
            return true;
        }

        const cleanName = name.replace(/\s\s+/g, ' ');
        if (cleanName.includes(query)) {
            return true;
        }

        const queryChunks = query.split(' ');
        return queryChunks.every((chunk) => cleanName.includes(chunk));
    });

    const checkEnter = (event: any) => {
        if (onEnter && event.key === 'Enter') {
            event.preventDefault();
            onEnter(event);
        }
    }

    const toggleOpen = (e: any) => {
        e.stopPropagation();

        if (!disabled) {
            setIsOpen((prevState) => !prevState)
        }
    }

    useEffect(() => {
        if (searchValue?.length > 0) {
            if (!isOpen) {
                setIsOpen(true);
            }
            let filteredData: any = [];
            if (propertiesApi) {
                propertiesApi.map((prop: string) => {
                    const filtered = data?.filter((item: any) => item[prop]?.toLowerCase().includes(searchValue.toLowerCase()));
                    filteredData.push(...filtered);
                })
            } else {
                if (data != null) {
                    const filtered = data?.filter((item: any) => item[defaultPropertyName === undefined ? 'name' : defaultPropertyName]?.toString()?.toLowerCase().includes(searchValue.toLowerCase()));
                    filteredData.push(...filtered);
                }
            }
            setFilteredData(filteredData);
            setPosition(ref.current?.children[0]?.getBoundingClientRect());
        } else {
            setFilteredData(data);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchValue, data, defaultPropertyName, propertiesApi]);

    useEffect(() => {
        setFilteredData(data);
        // defaultValue can also be 0 sometimes, e.g. as a value of an enum, that's why we need this extended check
        if ((defaultValue ?? undefined) !== undefined) {
            // in case we have defaultValue and also property inside array that comparable to the default value
            const preselected = Array.isArray(data) && data?.find((item: any) => item['id'] === defaultValue);
            setSelectedItem((propertiesApi && preselected) ? propertiesApi?.map((prop: any) => `${preselected[prop] ? preselected[prop] : ''} `) : preselected?.name);

            setSelected(preselected)
        }
        if (defaultValue === null) {
            setSelectedItem("");
            setSelected(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data, defaultValue, propertiesApi, defaultPropertyName]);

    useEffect(() => {
        if (ref.current !== null && isOpen) {
            setPosition(ref.current?.children[0]?.getBoundingClientRect());
            window.addEventListener("scroll", (e) => {
                setPosition(ref.current?.children[0]?.getBoundingClientRect());
            });

            // Close on Escape key
            const handleKeyDown = (e: any) => {
                if (e.key === "Escape") {
                    e.preventDefault();
                    setIsOpen(false);
                    onEscapePress && onEscapePress();
                }
            };
            window.addEventListener("keydown", handleKeyDown);

        } else {
            setPosition({})
        }
        return () => {
            window.removeEventListener("scroll", () => { });
            window.removeEventListener("keydown", () => { });
            setPosition({})
        }
    }, [isOpen, ref]);

    return (
        <div className={`${style.cSelect} ${customClass}`} ref={ref} style={styles}>
            {(!!title || showInfoIcon) && <div className={style.cSelect__header}>
                {!!title && <label htmlFor="" className={style.cSelect__label}>{title}</label>}
                {showInfoIcon && <TooltipV2 text={infoIconText} customClass={style.cSelect__infoIcon}><SvgInfo fill={Colors.Blue} /></TooltipV2>}
            </div>}
            <div
                ref={refInputWrap}
                onClick={(e) => toggleOpen(e)}
                className={`
                    ${style.cSelect__inputWrap} 
                    ${isError && style.cSelect__inputWrap____isError} 
                    ${disabled && style.cSelect__inputWrap____disabled}
                    ${isOpen && style.cSelect__inputWrap____visited}
                `}
            >
                <input
                    type="text"
                    value={inputFieldTouched ? searchValue : (selected?.name_translated ?? selected?.name ?? fakeSelection ?? '')}
                    placeholder={placeholder ?? t('select_items.input_placeholder')}
                    className={`${style.cSelect__inputField} ${selectedItem?.length > 0 && style.cSelect__inputField____withSelection} ${disabled || disableTyping ? style.cSelect__inputField____disableInput : ''}`}
                    onChange={(e: any) => {
                        changeSearchValue(e.target.value)
                        setInputFieldTouched(true);
                    }}
                    // onFocus={() => setIsOpen(true)}
                    disabled={disabled || disableTyping}
                    autoFocus={autoFocus}
                    autoComplete={disableAutoComplete ? "new-password" : undefined}
                    title={!ValidationHelper.isPropertyNullOrEmpty(searchValue) ? searchValue : (selected?.name ?? '')}
                    {...(!!onEnter ? { onKeyDown: (e) => { checkEnter(e) } } : {})}
                    ref={refInputField}
                    onBlur={() => setInputFieldTouched(false)}
                />
                <div className={style.cSelect__buttonsWrap}>
                    {((selected?.id || selected?.id === 0) && !disableRemoveAll) ? (
                        <TooltipV2 text={t('select_items.remove_selected_items')}>
                            <ButtonExpand
                                icon={<SvgCancel key="svg-cancel" />}
                                onClick={() => handleRemoveItem()}
                                disabled={disabled}
                            />
                        </TooltipV2>
                    ) : ''}
                    <ButtonExpand
                        icon={[!isOpen ? <SvgExpandMore key="svg-expand-more" /> : <SvgExpandLess key="svg-expand-less" />]}
                        onClick={(e) => toggleOpen(e)}
                        disabled={disabled}
                    />
                </div>
            </div>
            {!ValidationHelper.isPropertyNullOrEmpty(helpText) && (<span className={style.cSelect__description}>{helpText}</span>)}
            {(isError && !ValidationHelper.isPropertyNullOrEmpty(errorText)) && (<span className={style.cSelect__error}>{errorText}</span>)}
            {isClient && isOpen && (
                <div className={style.cSelect__dropdownWrap} tabIndex={-1}>
                    <div
                        ref={refKid}
                        className={[style.cSelect__dropdown, !isOpen && style.cSelect__dropdown__hide].join(' ')}
                        style={{
                            position: "fixed",
                            width: (position?.width && position?.width > 600) ? 600 : (position?.width || 0),
                            top: positionTop,
                            left: (position?.x || 0),
                            right: (isClient ? window.innerWidth - 100 : 0) < (position?.x || 0) ? "unset" : 16,
                            opacity: position.top ? 1 : 0
                        }}
                    >

                        <ul>
                            {isLoading && <div className={style.cSelect__dropdownMsg}>{t('loading_data')}</div>}
                            {data?.length === 0 && !isLoading && <div className={style.cSelect__dropdownMsg}>{noDataMessage ?? t('no_data')}</div>}
                            {typeof onSelectAll === 'function' && (
                                <li key="select-all">
                                    <ButtonDropdown
                                        text={t('select_all')}
                                        customClasses={`${style.cSelect__dropdownItemBtn} focusBtn-0`}
                                        onClick={() => onSelectAll?.(filteredData)}
                                        disabled={isLoading}
                                    />
                                </li>
                            )}
                            {matchingItems?.length > 0 ? (
                                matchingItems.map((item: any, index: number) => {
                                    const isSelected = selected?.id === item.id;
                                    if (item.component) {
                                        return <li
                                            ref={index === 0 ? firstItemRef : null}
                                            key={item.id + item.name}
                                            onClick={() => (isSelected && !disableRemoveAll) ? handleRemoveItem() : handleSelection(item)}>
                                            {item.component}
                                        </li>
                                    }
                                    if (!disabledItemsVisible && Array.isArray(disabledItems) && disabledItems.includes(item.id)) {
                                        return null;
                                    }
                                    return (
                                        <li key={item.id + item.name}>
                                            <ButtonDropdown
                                                text={item.name_translated ?? item.name}
                                                icon={isSelected && <SvgCheck key="svg-check" fill={Colors.Green} />}
                                                customClasses={`${style.cSelect__dropdownItemBtn} focusBtn-${index}`}
                                                onClick={() => (isSelected && !disableRemoveAll) ? handleRemoveItem() : handleSelection(item)}
                                                isSelected={isSelected}
                                                disabled={item?.disabled || (disabledItems && disabledItems.includes(item.id)) || isLoading}
                                            />
                                        </li>
                                    )
                                })
                            ) : (
                                !!searchTerm && <li className={style.cSelect__dropdownMsg}>{t('search_results.no_match')}</li>
                            )}
                        </ul>
                    </div>
                </div>
            )}
        </div>
    )
}

export default Select;
